import { CORE_WEBSOCKET_BASE_URL } from '../../core.tokens';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, EMPTY, merge, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, delay, distinctUntilChanged, filter, share, skip, switchMap, tap } from 'rxjs/operators';
import { webSocket } from 'rxjs/webSocket';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthMessageMap, AuthWebSocketResponse } from '../models/websocket.model';
import { NetworkService } from './network.service';
import { WatchdogService } from './watchdog.service';
import { AuthService } from './auth.service';
import { ClusterService } from './cluster.service';
import { Intercom } from './intercom.service';
import { isString } from '../utils';

@Injectable()
export class AuthWebsocketService {

  private readonly websocketBaseUrl = inject(CORE_WEBSOCKET_BASE_URL);
  private readonly destroyRef = inject(DestroyRef);
  private readonly network = inject(NetworkService);
  private readonly auth = inject(AuthService);
  private readonly intercom = inject(Intercom);
  private readonly cluster = inject(ClusterService);
  private readonly watchdog = inject(WatchdogService);

  private readonly logger = this.watchdog.tag('Auth WebSocket', 'magenta');

  private initialized = false;
  private wsSub: Subscription | null = null;
  private timeoutSub: Subscription | null = null;
  private heartbeatSub: Subscription | null = null;
  private handshakeSub: Subscription | null = null;

  private readonly statusSubject = new BehaviorSubject<boolean>(false);
  private readonly connectionCountSubject = new BehaviorSubject<number>(0);
  private readonly lastConnectionAtSubject = new BehaviorSubject<Date | null>(null);
  private readonly lastDisconnectionAtSubject = new BehaviorSubject<Date | null>(null);

  public readonly status$ = this.statusSubject.asObservable().pipe(distinctUntilChanged());
  public readonly connectionCount$ = this.connectionCountSubject.asObservable();
  public readonly lastConnectionAt$ = this.lastConnectionAtSubject.asObservable();
  public readonly lastDisconnectionAt$ = this.lastDisconnectionAtSubject.asObservable();

  private readonly ws$ = webSocket<AuthWebSocketResponse>({
    url: `${ this.websocketBaseUrl }/ws/auth`,
    openObserver: {
      next: () => {
        this.logger.info('Open');
        this.markConnected();
        this.handshake();
      },
    },
    closingObserver: {
      next: () => {
        this.logger.info('Closing');
        this.markDisconnected();
      },
    },
    closeObserver: {
      next: () => {
        this.logger.info('Close');
        this.markDisconnected();
      },
    },
  });

  private readonly messagesSubject = new Subject<AuthWebSocketResponse>();
  public readonly messages$ = this.messagesSubject.asObservable();

  public initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.logger.info('Initialize');

    this.status$.pipe(
      skip(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((status) => {
      if (status) {
        this.connectionCountSubject.next(this.connectionCountSubject.getValue() + 1);
        this.lastConnectionAtSubject.next(new Date());
      }
      else {
        this.lastDisconnectionAtSubject.next(new Date());
      }
    });

    combineLatest([
      this.cluster.leader$,
      this.auth.authorized$,
      this.network.status$.pipe(distinctUntilChanged()),
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(([leader, authorized, status]) => {
      if (!leader || authorized || !status) {
        this.unsubscribeAll();
        this.markDisconnected();
      }
      else {
        this.initWebSocket();
      }
    });
  }

  private handshake(): void {
    this.unsubscribeHandshake();

    this.handshakeSub = this.intercom.deviceSignature$.pipe(
      filter(isString),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((signature) => {
      this.send('deviceSignature', signature);
    });
  }

  private unsubscribeAll(): void {
    this.wsSub?.unsubscribe();
    this.wsSub = null;
    this.timeoutSub?.unsubscribe();
    this.timeoutSub = null;
    this.heartbeatSub?.unsubscribe();
    this.heartbeatSub = null;
  }

  private unsubscribeHandshake(): void {
    this.handshakeSub?.unsubscribe();
    this.handshakeSub = null;
  }

  private initWebSocket(): void {
    this.unsubscribeAll();

    const share$ = this.ws$.pipe(
      filter((response) => !!response?.type),
      catchError((error) => {
        this.logger.error(error);
        this.markDisconnected();
        return EMPTY;
      }),
      share(),
    );

    this.timeoutSub = merge(of('init'), share$).pipe(
      switchMap(() => of('timeout').pipe(delay(10_000))),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((msg) => {
      this.logger.error(msg);
      this.markDisconnected();
      this.initWebSocket();
    });

    this.heartbeatSub = timer(1000, 5000).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.logger.debug('Heartbeat send', new Date().toISOString());

      this.send('heartbeat', Date.now().toString());
    });

    this.wsSub = share$.pipe(
      tap((response) => this.messagesSubject.next(response)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      complete: () => {
        this.logger.log('Complete');
      },
    });
  }

  public send<K extends keyof AuthMessageMap>(type: K, data: AuthMessageMap[K]): void {
    this.logger.debug('Send', type, data);
    this.ws$.next({ type, data });
  }

  public complete(): void {
    this.ws$.complete();
    this.unsubscribeAll();
    this.markDisconnected();
  }

  private markConnected(): void {
    this.statusSubject.next(true);
  }

  private markDisconnected(): void {
    this.statusSubject.next(false);
  }

}
